"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, MessageSquareWarning, Signature, Scale, ArrowRightLeft, Map, Gavel, TrendingUp, UserMinus, Eraser, Files, GraduationCap, Shield, ChevronRight } from "lucide-react";
const categories = [{
  id: 1,
  title: "Athletic Performance",
  description: "High-level rehabilitation and strength training designed for competitive athletes and gym enthusiasts."
}, {
  id: 2,
  title: "Recovery & Longevity",
  description: "Expert care for post-operative transitions and maintaining mobility for an active family life."
}, {
  id: 3,
  title: "Specialized Screens",
  description: "Precision assessments including TPI Golf screens and quick movement check-ups to optimize your health."
}];
const servicesList = [{
  categoryId: 1,
  name: "Return to Sport",
  desc: "Get back to the game with high-performance rehab tailored to your specific sport and competitive goals.",
  icon: "TrendingUp"
}, {
  categoryId: 1,
  name: "Lift without Pain",
  desc: "Address gym-based injuries and implement prevention strategies to keep you strong and under the bar.",
  icon: "Shield"
}, {
  categoryId: 1,
  name: "Sports Performance",
  desc: "Advanced coaching to optimize power, speed, and durability for peak athletic output.",
  icon: "Gavel"
}, {
  categoryId: 2,
  name: "Keep up with Family",
  desc: "Improve mobility and reduce pain to stay active with your children and grandchildren without limitations.",
  icon: "Map"
}, {
  categoryId: 2,
  name: "Post-Op Recovery",
  desc: "Specialized rehab for ACL, shoulder, or joint replacements to ensure a safe and efficient return to activity.",
  icon: "Clock"
}, {
  categoryId: 2,
  name: "Joint Health",
  desc: "Targeted strategies to manage chronic discomfort and preserve long-term orthopedic health.",
  icon: "UserMinus"
}, {
  categoryId: 3,
  name: "TPI Golf Performance",
  desc: "A 16-point screen identifying physical limitations in your golf swing to improve your game and prevent injury.",
  icon: "GraduationCap"
}, {
  categoryId: 3,
  name: "30-Min Movement Check",
  desc: "A quick 'Toe-Touch' screen for those who want to move better and identify potential issues early.",
  icon: "Scale"
}, {
  categoryId: 3,
  name: "Injury Screening",
  desc: "Comprehensive assessment to detect biomechanical imbalances before they lead to pain.",
  icon: "MessageSquareWarning"
}, {
  categoryId: 4,
  name: "60-Minute 1-on-1",
  desc: "Dedicated hour-long sessions with Dr. Garaffa. No aides, no double-booking, just expert care.",
  icon: "Files"
}, {
  categoryId: 4,
  name: "Direct Access",
  desc: "NJ is a direct-access state, meaning you can start your recovery immediately without a doctor's script.",
  icon: "Signature"
}, {
  categoryId: 4,
  name: "Integrated Rehab App",
  desc: "24/7 access to your custom program and exercise videos to keep your progress on track.",
  icon: "ArrowRightLeft"
}];
const iconMap: Record<string, React.ElementType> = {
  Clock,
  MessageSquareWarning,
  Signature,
  Scale,
  ArrowRightLeft,
  Map,
  Gavel,
  TrendingUp,
  UserMinus,
  Eraser,
  Files,
  GraduationCap,
  Shield
};
export default function Services() {
  return <section data-section-id="174536" id="services" className="w-full flex flex-col bg-background text-foreground">
      {categories.map((category, index) => <div key={category.id} data-index={index} className={`w-full py-16 md:py-24 ${index % 2 === 1 ? 'bg-secondary/5' : 'bg-background'}`}>
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col items-center text-center mb-12 md:mb-16 max-w-3xl mx-auto">
              <Badge variant="outline" className="mb-4 border-secondary text-secondary uppercase tracking-wider font-default">
                Our Specialties
              </Badge>
              <h2 className="text-3xl md:text-4xl font-default font-bold text-primary mb-4">
                {category.title}
              </h2>
              <p className="text-lg text-muted-foreground font-default">
                {category.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {servicesList.filter(service => service.categoryId === category.id).map((service, sIndex) => {
            const IconComponent = iconMap[service.icon];
            return <Card key={service.name} data-index={sIndex} className="flex flex-col h-full bg-card border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group rounded-2xl">
                      <CardHeader className="pb-4">
                        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300 text-secondary">
                          {IconComponent && <IconComponent className="w-6 h-6" />}
                        </div>
                        <CardTitle className="text-xl font-default font-bold text-foreground">
                          {service.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <CardDescription className="text-base font-default text-muted-foreground">
                          {service.desc}
                        </CardDescription>
                      </CardContent>
                      <CardFooter className="pt-4 mt-auto border-t border-border/10">
                        <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-md font-bold tracking-wide shadow-sm group/btn">
                          <Link to="#">
                            Book Your Evaluation
                            <ChevronRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>;
          })}
            </div>
          </div>
        </div>)}
    </section>;
}